import type { PartialPick, PickDefined, SetRequired, Simplify } from '@eturi/ts-helpers'
import type { RawAllowance } from './Allowance.js'
import type { RawGeofence } from './GeofenceInfo.js'
import type { GranType } from './Granularity.js'
import type { Lang } from './Lang.js'
import type { RawRule } from './rules/Rule.js'
import type { RawUser } from './User.js'

// Server Model
export type AgeRange = [low: number, high: number]
export type OnboardingLabel = SetRequired<{ readonly [K in Lang]?: string }, 'en'>

// Base type containing common properties
type OnboardingBase = {
	readonly age_range: AgeRange
	readonly content_key: string
	readonly id: string
	readonly label: OnboardingLabel
	readonly order: number
}

type WithOnboardingBase<T, K extends keyof OnboardingBase> = Simplify<Pick<OnboardingBase, K> & T>

export type OnboardingAllowance = WithOnboardingBase<
	{
		readonly allowance: RawAllowance
		readonly allowance_enabled?: boolean
		readonly daily_limit: number
		readonly weekend_limit: number
	},
	'age_range'
>

export type OnboardingAppRule = WithOnboardingBase<
	{
		readonly directive: GranType
		readonly type: 'age_rating' | 'category'
	},
	'age_range' | 'id' | 'label' | 'order'
>

export type OnboardingConcern = Pick<OnboardingBase, 'id' | 'label' | 'order'>
export type OnboardGeofence = Pick<OnboardingBase, 'age_range' | 'content_key' | 'label' | 'order'>

export type OnboardingSchedule = WithOnboardingBase<
	PickDefined<RawRule, 'days' | 'time_duration'> & {
		readonly active?: boolean
		readonly rule_name: OnboardingLabel
	},
	'age_range' | 'content_key' | 'order'
>

export type OnboardingVew = WithOnboardingBase<
	{
		readonly selected: boolean
		readonly sublabel?: OnboardingLabel
		readonly view_enabled?: boolean
	},
	'age_range' | 'content_key' | 'label' | 'order'
>

export type Onboarding = {
	readonly allowance: OnboardingAllowance[]
	readonly app_rules: OnboardingAppRule[]
	readonly concerns: OnboardingConcern[]
	readonly geofences: OnboardGeofence[]
	readonly schedules: OnboardingSchedule[]
	readonly view: OnboardingVew[]
}

// Client Model
export type OnboardingUser = {
	readonly name: string
	readonly age: number
	readonly time_zone: string
}

export type OnboardingUsersState = {
	readonly [userName: string]: OnboardingUser
}

export type OnboardingClientData = {
	readonly concerns: OnboardingConcern[]
	readonly users: OnboardingUsersState
}

// Signup Model

// If geofences become  editable in the future, we'll use this
type OnboardingSignupGeofenceComplete = Simplify<
	PickDefined<RawGeofence, 'lat' | 'lon' | 'name' | 'radius' | 'triggers'> & {
		readonly active: true
		readonly incomplete: false
	}
>

type OnboardingSignupGeofenceIncomplete = Simplify<
	PickDefined<RawGeofence, 'name' | 'triggers'> & {
		readonly incomplete: true
	}
>

export type OnboardingSignupGeofence =
	| OnboardingSignupGeofenceComplete
	| OnboardingSignupGeofenceIncomplete

export type OnboardingSignupSchedule = Simplify<
	PickDefined<RawRule, 'days' | 'rule_name' | 'time_duration'> & PartialPick<RawRule, 'active'>
>

export type OnboardingSignupUser = Simplify<
	PickDefined<RawUser, 'time_zone' | 'user_name'> & {
		readonly age: number
		readonly allowance?: PickDefined<RawUser, 'allowance' | 'allowance_enabled'>
		readonly app_rules?: Pick<OnboardingAppRule, 'id' | 'directive'>[]
		readonly content_filter?: {
			readonly filter_adult_content: boolean
			readonly filter_blocked_apps: boolean
		}
		readonly geofences?: OnboardingSignupGeofence[]
		readonly geolocation?: PickDefined<RawUser, 'user_location_active'>
		readonly schedules?: OnboardingSignupSchedule[]
		readonly view?: PickDefined<RawUser, 'view_enabled'>
	}
>

export type OnboardingSignUpConcerns = {
	// List of selected concern IDs
	readonly selected: string[]
}

export type OnboardingSignupChoices = {
	// Selected concern IDs
	readonly concerns: OnboardingSignUpConcerns

	readonly state:
		| 'active' /* Active choices the server should try to fulfill */
		| 'inactive' /* Choices aren't active. Store document for future reference */
		| 'retired' /* Choices are retired. Do not use */
	readonly type: 'onboarding_choices'

	readonly users: OnboardingSignupUser[]

	// From SystemInfo.onboarding_choices.version
	readonly version: string
}

// Helper Functions
type WithAgeRange = Pick<OnboardingBase, 'age_range'>

/** Determines whether an age is within an age range. */
export const matchesAgeRange = ([low, high]: AgeRange, age: number) => age >= low && age <= high

/** Gets the label for an onboarding item in the given language, falling back to English. */
export const getOnboardingLabel = (label: OnboardingLabel, lang: Lang) => label[lang] || label.en

/** Filters onboarding items by age. */
export const filterOnboardingByAge = <T extends WithAgeRange>(data: T[], age: number) =>
	data.filter((item) => matchesAgeRange(item.age_range, age))

/** Finds the first onboarding item that matches an age. */
export const findOnboardingByAge = <T extends WithAgeRange>(data: T[], age: number) =>
	data.find((item) => matchesAgeRange(item.age_range, age))
